import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material'
import { Grid } from '@mui/material'
import { PlayArrow, Edit, MonetizationOn, Tv } from '@mui/icons-material'
import { Sidebar } from '../components/Sidebar'

declare global {
  interface ImportMeta {
    env: {
      VITE_API_BASE_URL: string;
    };
  }
}

interface User {
  id: string
  username: string
  role: string
}

interface TV {
  id: string
  name: string
  price?: number
  status: string
  rentalHours?: string
  startTime?: string
  endTime?: string
  cost?: string
}

interface DashboardPageProps {
  user: User | null
  onLogout: () => void
  onNavigate: (page: string) => void
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ user, onLogout, onNavigate }) => {
  const [tvs, setTvs] = useState<TV[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTVs()
  }, [])

  const fetchTVs = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/sheets/tvs`)
      if (response.ok) {
        const data = await response.json() as { tvs?: TV[] }
        setTvs(data.tvs || [])
      }
    } catch (error) {
      console.error('Error fetching TVs:', error)
    } finally {
      setLoading(false)
    }
  }

  // Dummy data for summary
  const activeRentals = 0
  const availableTVs = tvs.filter(tv => tv.status === 'available').length
  const totalRevenue = 149000

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f7' }}>
      {/* Sidebar */}
      <Sidebar 
        user={user}
        currentPage="dashboard"
        onNavigate={onNavigate}
        onLogout={onLogout}
      />
      {/* Main Content */}
      <Box sx={{ flex: 1, p: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={700} color="#3d36fa" sx={{ mr: 2 }}>
            Dashboard
          </Typography>
        </Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#ffcb3c', borderRadius: 4 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} color="#b28704">Active Rentals</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="h4" fontWeight={700} color="#b28704">{activeRentals}</Typography>
                  <PlayArrow sx={{ color: '#b28704', ml: 1, fontSize: 32 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#3ec46d', borderRadius: 4 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} color="white">Available TVs</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="h4" fontWeight={700} color="white">{availableTVs}</Typography>
                  <Tv sx={{ color: 'white', ml: 1, fontSize: 32 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#ff9800', borderRadius: 4 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} color="white">Total Revenue</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="h4" fontWeight={700} color="white">Rp{totalRevenue.toLocaleString()}</Typography>
                  <MonetizationOn sx={{ color: 'white', ml: 1, fontSize: 32 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* Rental Management Table */}
        <Paper sx={{ borderRadius: 4, p: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Rental Management
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Price/Hour</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rental Hours</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tvs.map((tv) => (
                  <TableRow key={tv.id}>
                    <TableCell>{tv.name}</TableCell>
                    <TableCell>{tv.price ? `Rp${tv.price.toLocaleString()}` : '-'}</TableCell>
                    <TableCell>{tv.status || '-'}</TableCell>
                    <TableCell>{tv.rentalHours || '-'}</TableCell>
                    <TableCell>{tv.startTime || '-'}</TableCell>
                    <TableCell>{tv.endTime || '-'}</TableCell>
                    <TableCell>{tv.cost || '-'}</TableCell>
                    <TableCell>
                      <IconButton color="primary" size="small"><Edit /></IconButton>
                      <IconButton color="success" size="small"><PlayArrow /></IconButton>
                      {/* Tombol remote dihapus/hide */}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  )
} 